import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { toPng } from 'html-to-image';
import JSZip from 'jszip';
import { api } from '../../app/http-common';
import { useHasFeature } from '../../hooks/useFrontegg';
import SlideCanvas, { SlideCanvasOffscreen } from './components/SlideCanvas';
import ControlsSidebar from './components/ControlsSidebar';
import CloudinaryImagePicker from './components/CloudinaryImagePicker';
import ScheduleModal from './components/ScheduleModal';
import AddSlideModal from './components/AddSlideModal';
import HelpModal from './components/HelpModal';
import useSlideHistory from './hooks/useSlideHistory';

const VALID_TRANSITIONS = {
  draft: ['ready'],
  ready: ['draft', 'approved'],
  approved: ['draft'],
  failed: ['draft'],
};

function interpolateColor(c1, c2, t) {
  const r1 = parseInt(c1.slice(1, 3), 16), g1 = parseInt(c1.slice(3, 5), 16), b1 = parseInt(c1.slice(5, 7), 16);
  const r2 = parseInt(c2.slice(1, 3), 16), g2 = parseInt(c2.slice(3, 5), 16), b2 = parseInt(c2.slice(5, 7), 16);
  const r = Math.round(r1 + (r2 - r1) * t), g = Math.round(g1 + (g2 - g1) * t), b = Math.round(b1 + (b2 - b1) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export default function SocialPostEditorV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isEntitled, isLoading: featureLoading } = useHasFeature('social-posts-creation');

  // ── Data state ──
  const [post, setPost] = useState(null);
  const { slides, setSlides, undo, redo, canUndo, canRedo, replaceSlides } = useSlideHistory([]);
  const [themes, setThemes] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // ── Editor state ──
  const [selectedSlideIdx, setSelectedSlideIdx] = useState(0);
  const [editCaption, setEditCaption] = useState('');
  const [editHashtags, setEditHashtags] = useState('');
  const [format, setFormat] = useState('post');
  const [font, setFont] = useState('display');
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [gradientFrom, setGradientFrom] = useState('#1a1a2e');
  const [gradientTo, setGradientTo] = useState('#0f3460');
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [addSlideModalOpen, setAddSlideModalOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [lastEditedBy, setLastEditedBy] = useState('');
  const [localVersion, setLocalVersion] = useState(0);
  const localVersionRef = useRef(0); // mirror for async callbacks that shouldn't depend on state

  // lastEditedBy may be a plain userId string or a populated {_id, name, email} object
  const resolveEditedBy = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    return v.name || v.email || v._id || '';
  };
  const [conflictDetected, setConflictDetected] = useState(false);

  // ── Action state ──
  const [saving, setSaving] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [metaConnected, setMetaConnected] = useState(false);

  // ── Refs for export ──
  const slidePreviewRef = useRef(null);
  const offscreenRefs = useRef([]);

  // ── Debounced auto-save for slide edits ──
  const slideSaveTimer = useRef(null);
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  // ── Load data ──
  const loadPost = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getSocialPost(id);
      setPost(data.post);
      const slidesWithDefaults = (data.slides || []).map((s) => ({
        ...s,
        background: s.background || '#1a1a2e',
        textColor: s.textColor || '#ffffff',
        bodyText: s.bodyText || '',
      }));
      replaceSlides(slidesWithDefaults);
      setLocalVersion(data.post.version || 0);
      localVersionRef.current = data.post.version || 0;
      setLastEditedBy(resolveEditedBy(data.post.lastEditedBy));
      setEditCaption(data.post.caption || '');
      setEditHashtags((data.post.hashtags || []).join(', '));
      setSelectedChannelId(data.post.channel?._id || data.post.channel || '');
      // Check Meta connection for this channel
      const chId = data.post.channel?._id || data.post.channel;
      if (chId) {
        api.getMetaStatus(chId).then(res => {
          setMetaConnected(res?.data?.connected || false);
        }).catch(() => {});
      }
    } catch (err) {
      setError(err.message || 'Failed to load post');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadThemes = useCallback(async () => {
    try {
      const data = await api.getSocialThemes();
      setThemes(data?.themes || []);
    } catch (_) {}
  }, []);

  const loadChannels = useCallback(async () => {
    try {
      const data = await api.getSocialChannels();
      setChannels(data?.channels || []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (isEntitled) {
      loadPost();
      loadThemes();
      loadChannels();
    }
  }, [isEntitled, loadPost, loadThemes, loadChannels]);

  // ── Collaboration polling ──
  useEffect(() => {
    if (!id || !isEntitled || loading) return;
    const interval = setInterval(async () => {
      try {
        const data = await api.getSocialPostVersion(id, { silent: true });
        if (data.version > localVersionRef.current) {
          const editedById = resolveEditedBy(data.lastEditedBy);
          setLastEditedBy(editedById);
          setLocalVersion(data.version);
          localVersionRef.current = data.version;
        }
      } catch (_) {}
    }, 10000);
    return () => clearInterval(interval);
  }, [id, isEntitled, loading]);

  // ── Helpers ──
  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const currentSlide = slides[selectedSlideIdx] || null;
  const channelHandle = channels.find((c) => c._id === selectedChannelId)?.handle || '';

  // ── Slide manipulation ──
  const handleUpdateSlide = useCallback((idx, patch) => {
    setSlides((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

    // Auto-save editable fields after 800ms of inactivity
    const SAVEABLE_KEYS = ['headline', 'bodyText', 'background', 'textColor', 'backgroundImage', 'animationType', 'overlayImages', 'elementPositions'];
    const savePayload = {};
    for (const key of SAVEABLE_KEYS) {
      if (patch[key] !== undefined) savePayload[key] = patch[key];
    }
    if (Object.keys(savePayload).length > 0) {
      clearTimeout(slideSaveTimer.current);
      slideSaveTimer.current = setTimeout(async () => {
        const slide = slidesRef.current[idx];
        if (slide?._id && !slide._id.startsWith('temp-')) {
          try {
            const resp = await api.updateSocialSlide(id, slide._id, savePayload);
            if (resp.version) {
              setLocalVersion(resp.version);
              localVersionRef.current = resp.version;
            }
          } catch (err) {
            if (err?.response?.status === 409) setConflictDetected(true);
          }
        }
      }, 800);
    }
  }, [id]);

  const handleAddSlide = () => {
    const newSlide = {
      _id: `temp-${Date.now()}`,
      headline: 'New Slide',
      bodyText: '',
      background: '#1a1a2e',
      textColor: '#ffffff',
      type: 'content',
      order: slides.length,
    };
    setSlides([...slides, newSlide]);
    setSelectedSlideIdx(slides.length);
  };

  const handleDeleteSlide = (idx) => {
    if (slides.length <= 1) return;
    setSlides(slides.filter((_, i) => i !== idx));
    setSelectedSlideIdx(Math.max(0, idx - 1));
  };

  // ── Reorder slides ──
  const handleReorderSlides = useCallback((reordered) => {
    setSlides(reordered);
    // Persist new ordering
    const ids = reordered.map((s) => s._id).filter((sid) => sid && !sid.startsWith('temp-'));
    if (ids.length > 0) {
      api.reorderSocialSlides(id, ids).catch(() => {});
    }
  }, [id, setSlides]);

  // ── Add slide from template ──
  const handleAddFromTheme = (themeId) => {
    const theme = themes.find((t) => t._id === themeId);
    const newSlide = {
      _id: `temp-${Date.now()}`,
      headline: theme?.name || 'New Slide',
      bodyText: '',
      background: theme?.gradientColors?.[0] || '#1a1a2e',
      textColor: theme?.textColor || '#ffffff',
      type: 'content',
      order: slides.length,
    };
    setSlides([...slides, newSlide]);
    setSelectedSlideIdx(slides.length);
    setAddSlideModalOpen(false);
  };

  // ── Schedule / Unschedule ──
  const handleSchedule = async (isoString) => {
    setError('');
    try {
      const data = await api.updateSocialPost(id, { scheduledAt: isoString });
      setPost(data.post);
      setScheduleModalOpen(false);
      showSuccess('Post scheduled');
    } catch (err) {
      setError(err.message || 'Failed to schedule');
    }
  };

  const handleUnschedule = async () => {
    setError('');
    try {
      const data = await api.unscheduleSocialPost(id);
      setPost(data.post);
      setScheduleModalOpen(false);
      showSuccess('Schedule removed');
    } catch (err) {
      setError(err.message || 'Failed to unschedule');
    }
  };

  // ── Credit change ──
  const handleCreditChange = async (assetId, creditData) => {
    try {
      await api.updateSocialAsset(id, assetId, creditData);
    } catch (_) {}
  };

  // ── Caption & save ──
  const handleSaveCaption = async () => {
    if (!post) return;
    setSaving(true);
    setError('');
    try {
      const hashtags = editHashtags.split(',').map((t) => t.trim()).filter(Boolean);
      const data = await api.updateSocialPost(id, { caption: editCaption, hashtags, expectedVersion: localVersion });
      setPost(data.post);
      const newVer = data.post.version || localVersion + 1;
      setLocalVersion(newVer);
      localVersionRef.current = newVer;
      showSuccess('Saved');
    } catch (err) {
      if (err?.response?.status === 409) {
        setConflictDetected(true);
      } else {
        setError(err.message || 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateCaption = async () => {
    setSaving(true);
    setError('');
    try {
      const data = await api.regenerateSocialCaption(id);
      setPost(data.post);
      setEditCaption(data.post.caption || '');
      setEditHashtags((data.post.hashtags || []).join(', '));
      showSuccess('Caption regenerated');
    } catch (err) {
      setError(err.message || 'Failed to regenerate');
    } finally {
      setSaving(false);
    }
  };

  // ── Rendering (server → client-side fallback) ──
  const handleRender = async (mode) => {
    setRendering(true);
    setError('');
    try {
      const opts = { mode };
      if (mode === 'gradient') opts.gradientColors = [gradientFrom, gradientTo];
      const data = await api.renderSocialSlides(id, opts);
      replaceSlides(
        (data.slides || []).map((s) => ({
          ...s,
          background: s.background || '#1a1a2e',
          textColor: s.textColor || '#ffffff',
          bodyText: s.bodyText || '',
        }))
      );
      showSuccess(mode === 'gradient' ? 'Gradient slides rendered' : 'Slides rendered');
    } catch (err) {
      // Fallback: client-side render via html-to-image
      try {
        if (offscreenRefs.current.length > 0) {
          for (let i = 0; i < slides.length; i++) {
            const el = offscreenRefs.current[i];
            if (!el) continue;
            const dataUrl = await toPng(el, { cacheBust: true, pixelRatio: 2 });
            // Download the rendered PNG
            const link = document.createElement('a');
            link.download = `slide-${i + 1}.png`;
            link.href = dataUrl;
            link.click();
          }
          showSuccess('Slides rendered locally (server unavailable)');
        } else {
          setError(err.message || 'Failed to render');
        }
      } catch (fallbackErr) {
        setError(err.message || 'Failed to render');
      }
    } finally {
      setRendering(false);
    }
  };

  // ── Server-side export ──
  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      await api.exportSocialPost(id);
      showSuccess('Export started...');
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const data = await api.getSocialPostDownload(id);
          if (data.downloadUrl) {
            clearInterval(poll);
            setExporting(false);
            window.open(data.downloadUrl, '_blank');
            loadPost();
          } else if (attempts >= 30) {
            clearInterval(poll);
            setExporting(false);
            setError('Export timed out.');
          }
        } catch (_) {
          if (attempts >= 30) {
            clearInterval(poll);
            setExporting(false);
            setError('Export timed out.');
          }
        }
      }, 2000);
    } catch (err) {
      // Fallback to client-side ZIP download when server export fails
      try {
        await handleQuickDownload();
        showSuccess('Server export unavailable — downloaded locally');
      } catch (_) {
        setError(err.message || 'Failed to export');
      }
      setExporting(false);
    }
  };

  // ── Client-side quick download ──
  const handleQuickDownload = async () => {
    if (!offscreenRefs.current.length) return;
    try {
      const zip = new JSZip();
      const folder = zip.folder('carousel-slides');
      for (let i = 0; i < slides.length; i++) {
        const el = offscreenRefs.current[i];
        if (!el) continue;
        const dataUrl = await toPng(el, { cacheBust: true, pixelRatio: 2 });
        const base64 = dataUrl.split(',')[1];
        folder.file(`slide-${i + 1}.png`, base64, { base64: true });
      }
      // Add caption.txt
      const captionText = [editCaption, '', (post?.hashtags || []).join(' ')].join('\n');
      folder.file('caption.txt', captionText);

      const blob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.download = `social-carousel-${id}.zip`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
      showSuccess('ZIP downloaded');
    } catch (err) {
      setError('Quick download failed: ' + (err.message || ''));
    }
  };

  // ── Status ──
  const handleStatusChange = async (newStatus) => {
    setError('');
    try {
      const data = await api.updateSocialPostStatus(id, newStatus);
      setPost(data.post);
      showSuccess(`Status: ${newStatus}`);
    } catch (err) {
      setError(err.message || 'Failed to update status');
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!window.confirm('Delete this social post?')) return;
    try {
      await api.deleteSocialPost(id);
      navigate('/social-posts');
    } catch (err) {
      setError(err.message || 'Failed to delete');
    }
  };

  // ── Upload asset ──
  const handleUploadAsset = async (file, slideId) => {
    setError('');
    try {
      const data = await api.uploadSocialAsset(id, file, slideId);
      await loadPost();
      if (data.newSlideIndex !== undefined) setSelectedSlideIdx(data.newSlideIndex);
      showSuccess(slideId ? 'Image replaced' : 'New slide added');
    } catch (err) {
      setError(err.message || 'Failed to upload');
    }
  };

  // ── Channel change ──
  const handleChannelChange = async (channelId) => {
    setSelectedChannelId(channelId);
    setMetaConnected(false);
    try {
      await api.updateSocialPost(id, { channel: channelId || null });
      // Check Meta connection status for the new channel
      if (channelId) {
        const res = await api.getMetaStatus(channelId);
        setMetaConnected(res?.data?.connected || false);
      }
    } catch (_) {}
  };

  // ── Apply theme ──
  const handleApplyTheme = (themeId) => {
    const theme = themes.find((t) => t._id === themeId);
    if (!theme) return;
    setSlides((prev) => prev.map((s) => ({
      ...s,
      background: theme.gradientColors?.[0] || s.background,
      textColor: theme.textColor || s.textColor,
    })));
    if (theme.gradientColors?.length >= 2) {
      setGradientFrom(theme.gradientColors[0]);
      setGradientTo(theme.gradientColors[1]);
    }
  };

  // ── Apply property to all slides ──
  const handleApplyToAll = useCallback((patch) => {
    setSlides((prev) => prev.map((s) => ({ ...s, ...patch })));
    clearTimeout(slideSaveTimer.current);
    slideSaveTimer.current = setTimeout(async () => {
      for (const slide of slidesRef.current) {
        if (slide._id && !slide._id.startsWith('temp-')) {
          try {
            const resp = await api.updateSocialSlide(id, slide._id, patch);
            if (resp.version) {
              setLocalVersion(resp.version);
              localVersionRef.current = resp.version;
            }
          } catch (_) {}
        }
      }
    }, 800);
  }, [id, setSlides]);

  // ── Apply gradient interpolation across all slides ──
  const handleApplyGradient = useCallback(() => {
    setSlides((prev) => {
      if (prev.length === 0) return prev;
      if (prev.length === 1) return [{ ...prev[0], background: gradientFrom }];
      return prev.map((s, i) => ({
        ...s,
        background: interpolateColor(gradientFrom, gradientTo, i / (prev.length - 1)),
      }));
    });
    clearTimeout(slideSaveTimer.current);
    slideSaveTimer.current = setTimeout(async () => {
      for (const slide of slidesRef.current) {
        if (slide._id && !slide._id.startsWith('temp-')) {
          try {
            const resp = await api.updateSocialSlide(id, slide._id, { background: slide.background });
            if (resp.version) {
              setLocalVersion(resp.version);
              localVersionRef.current = resp.version;
            }
          } catch (_) {}
        }
      }
    }, 800);
  }, [id, gradientFrom, gradientTo, setSlides]);

  // ── Publish to Meta ──
  const handlePublish = async () => {
    if (!selectedChannelId) {
      setError('Please select a channel first.');
      return;
    }
    setPublishing(true);
    setError('');
    try {
      const result = await api.publishSocialPost(id);
      setSuccessMsg(`Published to ${result.platform || 'Meta'} successfully!`);
      setPost((prev) => ({
        ...prev,
        scheduledStatus: 'published',
        publishedAt: new Date().toISOString(),
        status: 'approved',
      }));
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  // ── Image picker callback ──
  const handleImageSelect = (imageData) => {
    if (!imageData?.secureUrl) return;
    handleUpdateSlide(selectedSlideIdx, { backgroundImage: imageData.secureUrl });
    setImagePickerOpen(false);
  };

  // ── Guard renders ──
  if (featureLoading) return <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-zinc-400">Loading...</div>;
  if (!isEntitled) return <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-zinc-400">Social Posts feature is not available for your account.</div>;
  if (loading) return <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-zinc-400">Loading post...</div>;
  if (error && !post) return <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-red-400">{error}</div>;
  if (!post) return <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-text-red-400">Post not found</div>;

  const availableTransitions = VALID_TRANSITIONS[post.status] || [];

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-bg-zinc-950 tw-text-white">
      {/* ── Header ── */}
      <header className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-3 tw-border-b tw-border-zinc-800 tw-bg-zinc-950/80 tw-backdrop-blur-xl tw-shrink-0">
        <div className="tw-flex tw-items-center tw-gap-4">
          <button
            onClick={() => navigate('/social-posts')}
            className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-zinc-500 hover:tw-text-white tw-transition-colors"
          >
            ← Back
          </button>
          <h1 className="tw-text-sm tw-font-bold tw-truncate tw-max-w-md">
            {post.story?.title || post.story?.subject || 'Untitled Post'}
          </h1>
        </div>
        <div className="tw-flex tw-items-center tw-gap-3">
          <button
            onClick={() => setHelpOpen(true)}
            className="tw-w-6 tw-h-6 tw-rounded-full tw-border tw-border-zinc-600 tw-text-zinc-400 hover:tw-text-white hover:tw-border-zinc-400 tw-transition-colors tw-text-xs tw-font-bold tw-flex tw-items-center tw-justify-center"
            title="How to use Social Posts"
          >
            ?
          </button>
          <span className={`tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-px-2 tw-py-1 tw-rounded-full ${
            post.status === 'approved' ? 'tw-bg-green-500/20 tw-text-green-400'
            : post.status === 'ready' ? 'tw-bg-yellow-500/20 tw-text-yellow-400'
            : post.status === 'failed' ? 'tw-bg-red-500/20 tw-text-red-400'
            : 'tw-bg-zinc-700/50 tw-text-zinc-400'
          }`}>
            {post.status}
          </span>
          <span className="tw-text-[10px] tw-text-zinc-600">{post.platform}</span>
          {post.scheduledStatus === 'scheduled' && post.scheduledAt && (
            <span className="tw-text-[10px] tw-text-purple-400 tw-bg-purple-500/10 tw-px-2 tw-py-0.5 tw-rounded-full">
              📅 {new Date(post.scheduledAt).toLocaleString()}
            </span>
          )}
          {post.scheduledStatus === 'published' && (
            <span className="tw-text-[10px] tw-text-green-400 tw-bg-green-500/10 tw-px-2 tw-py-0.5 tw-rounded-full">
              ✓ Published
            </span>
          )}
        </div>
      </header>

      {/* ── Messages ── */}
      {conflictDetected && (
        <div className="tw-mx-6 tw-mt-2 tw-px-4 tw-py-2 tw-bg-orange-500/10 tw-border tw-border-orange-500/30 tw-rounded-lg tw-text-orange-400 tw-text-sm tw-flex tw-items-center tw-justify-between">
          <span>⚠️ This post was edited by someone else. Your changes may overwrite theirs.</span>
          <button
            onClick={() => { setConflictDetected(false); loadPost(); }}
            className="tw-btn-secondary tw-text-xs tw-ml-3 tw-whitespace-nowrap"
          >
            Reload Latest
          </button>
        </div>
      )}
      {error && (
        <div className="tw-mx-6 tw-mt-2 tw-px-4 tw-py-2 tw-bg-red-500/10 tw-border tw-border-red-500/30 tw-rounded-lg tw-text-red-400 tw-text-sm">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="tw-mx-6 tw-mt-2 tw-px-4 tw-py-2 tw-bg-green-500/10 tw-border tw-border-green-500/30 tw-rounded-lg tw-text-green-400 tw-text-sm">
          {successMsg}
        </div>
      )}

      {/* ── Main body: Sidebar + Canvas ── */}
      <div className="tw-flex tw-flex-1 tw-overflow-hidden tw-gap-4 tw-p-4">
        {/* Controls sidebar (left) */}
        <ControlsSidebar
          slides={slides}
          selectedSlideIdx={selectedSlideIdx}
          onSelectSlide={setSelectedSlideIdx}
          onUpdateSlide={handleUpdateSlide}
          onAddSlide={handleAddSlide}
          onDeleteSlide={handleDeleteSlide}
          onReorderSlides={handleReorderSlides}
          caption={editCaption}
          hashtags={editHashtags}
          onCaptionChange={setEditCaption}
          onHashtagsChange={setEditHashtags}
          onSaveCaption={handleSaveCaption}
          onRegenerateCaption={handleRegenerateCaption}
          format={format}
          onFormatChange={setFormat}
          font={font}
          onFontChange={setFont}
          channels={channels}
          selectedChannelId={selectedChannelId}
          onChannelChange={handleChannelChange}
          themes={themes}
          onApplyTheme={handleApplyTheme}
          onRender={handleRender}
          onExport={handleExport}
          onQuickDownload={handleQuickDownload}
          onDelete={handleDelete}
          onStatusChange={handleStatusChange}
          availableTransitions={availableTransitions}
          status={post.status}
          onUploadAsset={handleUploadAsset}
          onOpenImagePicker={() => setImagePickerOpen(true)}
          onOpenAddSlide={() => setAddSlideModalOpen(true)}
          onOpenSchedule={() => setScheduleModalOpen(true)}
          onCreditChange={handleCreditChange}
          saving={saving}
          rendering={rendering}
          exporting={exporting}
          gradientFrom={gradientFrom}
          gradientTo={gradientTo}
          onGradientFromChange={setGradientFrom}
          onGradientToChange={setGradientTo}
          post={post}
          onRefreshFromStory={loadPost}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={undo}
          onRedo={redo}
          lastEditedBy={lastEditedBy}
          onApplyToAll={handleApplyToAll}
          onApplyGradient={handleApplyGradient}
          onPublish={handlePublish}
          publishing={publishing}
          metaConnected={metaConnected}
        />

        {/* Canvas area (right) */}
        <div className="tw-flex-1 tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-zinc-900 tw-rounded-2xl tw-border tw-border-zinc-800 tw-p-6 tw-relative tw-overflow-hidden">
          {/* Label */}
          <div className="tw-absolute tw-top-4 tw-left-4 tw-text-[10px] tw-font-mono tw-uppercase tw-tracking-widest tw-text-zinc-600 tw-flex tw-items-center tw-gap-1">
            <span className="tw-text-social-pink">✦</span> Live Preview
          </div>

          {/* Slide navigation */}
          <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
            <button
              onClick={() => setSelectedSlideIdx(Math.max(0, selectedSlideIdx - 1))}
              disabled={selectedSlideIdx === 0}
              className="tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-zinc-800 disabled:tw-opacity-20 tw-transition-colors tw-text-zinc-400"
            >
              ‹
            </button>
            <div className="tw-flex tw-gap-1">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedSlideIdx(i)}
                  className={`tw-h-1.5 tw-rounded-full tw-transition-all ${
                    i === selectedSlideIdx ? 'tw-w-6 tw-bg-social-pink' : 'tw-w-2 tw-bg-zinc-700 hover:tw-bg-zinc-600'
                  }`}
                />
              ))}
            </div>
            <button
              onClick={() => setSelectedSlideIdx(Math.min(slides.length - 1, selectedSlideIdx + 1))}
              disabled={selectedSlideIdx === slides.length - 1}
              className="tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-zinc-800 disabled:tw-opacity-20 tw-transition-colors tw-text-zinc-400"
            >
              ›
            </button>
          </div>

          {/* The slide canvas */}
          <AnimatePresence mode="wait">
            {currentSlide && (
              <SlideCanvas
                key={selectedSlideIdx}
                ref={slidePreviewRef}
                slide={currentSlide}
                slideIndex={selectedSlideIdx}
                totalSlides={slides.length}
                format={format}
                font={font}
                channelHandle={channelHandle}
                previewSize={format === 'story' ? 340 : 440}
                onOverlayMove={(ovIdx, pos) => {
                  const overlays = [...(currentSlide.overlayImages || [])];
                  overlays[ovIdx] = { ...overlays[ovIdx], ...pos };
                  handleUpdateSlide(selectedSlideIdx, { overlayImages: overlays });
                }}
                onElementMove={(type, pos) => {
                  const positions = { ...(currentSlide.elementPositions || {}), [type]: pos };
                  handleUpdateSlide(selectedSlideIdx, { elementPositions: positions });
                }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Off-screen container for high-res PNG capture */}
      <SlideCanvasOffscreen
        slides={slides}
        format={format}
        font={font}
        channelHandle={channelHandle}
        refs={offscreenRefs}
      />

      {/* Cloudinary Media Library image picker */}
      <CloudinaryImagePicker
        open={imagePickerOpen}
        onClose={() => setImagePickerOpen(false)}
        onSelect={handleImageSelect}
        folder="alchemiq-social/brand-images"
      />

      {/* Schedule modal */}
      <ScheduleModal
        open={scheduleModalOpen}
        currentDate={post?.scheduledAt ? new Date(post.scheduledAt) : null}
        onSchedule={handleSchedule}
        onUnschedule={handleUnschedule}
        onClose={() => setScheduleModalOpen(false)}
      />

      {/* Add slide modal */}
      <AddSlideModal
        open={addSlideModalOpen}
        themes={themes}
        onAddBlank={() => { handleAddSlide(); setAddSlideModalOpen(false); }}
        onAddFromTheme={handleAddFromTheme}
        onClose={() => setAddSlideModalOpen(false)}
      />

      {/* Help modal */}
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} context="editor" />
    </div>
  );
}
